import React from 'react';

export interface UseSetActions<K> {
  add: (key: K) => void;
  remove: (key: K) => void;
  toggle: (key: K) => void;
  reset: () => void;
  clear: () => void;
  has: (key: K) => boolean;
}

export const useSet = <K>(initialSet = new Set<K>()): [Set<K>, UseSetActions<K>] => {
  const initialSetRef = React.useRef(initialSet);
  const [set, setSet] = React.useState(initialSet);

  const add = React.useCallback((item: K) => {
    setSet((prevSet) => new Set([...Array.from(prevSet), item]));
  }, []);

  const remove = React.useCallback((item: K) => {
    setSet((prevSet) => new Set(Array.from(prevSet).filter((i) => i !== item)));
  }, []);

  const toggle = React.useCallback((item: K) => {
    setSet((prevSet) =>
      prevSet.has(item)
        ? new Set(Array.from(prevSet).filter((i) => i !== item))
        : new Set([...Array.from(prevSet), item]),
    );
  }, []);

  const reset = React.useCallback(() => {
    setSet(initialSetRef.current);
  }, []);

  const clear = React.useCallback(() => {
    setSet(new Set());
  }, []);

  const has = React.useCallback(
    (item: K) => {
      return set.has(item);
    },
    [set],
  );

  const utils = React.useMemo(() => {
    return {
      add,
      remove,
      toggle,
      reset,
      clear,
      has,
    } as UseSetActions<K>;
  }, [add, remove, toggle, reset, clear, has]);

  return [set, utils];
};
